import React from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Badge } from 'react-bootstrap';
import { useQuery } from 'react-query';
import { promptsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Plus, Search, Tag, Users, Eye, Copy, Heart } from 'lucide-react';

const DashboardPage = () => {
  const { user } = useAuth();

  // Fetch recent prompts
  const { data: promptsData, isLoading } = useQuery(
    ['prompts', 'recent'],
    () => promptsAPI.getPrompts({ limit: 5, sortBy: 'createdAt', sortOrder: 'desc' }),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );

  const recentPrompts = promptsData?.data || [];

  // Fetch dashboard statistics
  const { data: statsData, isLoading: isLoadingStats } = useQuery(
    ['prompts', 'stats'],
    promptsAPI.getStats,
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );

  const stats = [
    {
      title: 'Gesamtzahl Prompts',
      value: statsData?.data?.total || 0,
      icon: <i className="fas fa-brain text-primary"></i>,
      color: 'primary'
    },
    {
      title: 'Öffentliche Prompts',
      value: statsData?.data?.public || 0, // Assuming a 'public' field in statsData
      icon: <i className="fas fa-globe text-success"></i>,
      color: 'success'
    },
    {
      title: 'Team-Prompts',
      value: statsData?.data?.team || 0, // Assuming a 'team' field in statsData
      icon: <i className="fas fa-users text-info"></i>,
      color: 'info'
    },
    {
      title: 'Gesamtaufrufe',
      value: statsData?.data?.totalViews || 0,
      icon: <i className="fas fa-eye text-warning"></i>,
      color: 'warning'
    }
  ];

  const quickActions = [
    {
      title: 'Neuen Prompt erstellen',
      description: 'Beginne mit der Erstellung eines neuen KI-Prompts',
      icon: <Plus size={24} />,
      link: '/prompts/create',
      variant: 'primary'
    },
    {
      title: 'Prompts durchsuchen',
      description: 'Erkunde vorhandene Prompts',
      icon: <Search size={24} />,
      link: '/prompts',
      variant: 'outline-primary'
    },
    {
      title: 'Tags verwalten',
      description: 'Organisiere deine Prompt-Tags',
      icon: <Tag size={24} />,
      link: '/tags',
      variant: 'outline-secondary'
    }
  ];

  return (
    <Container fluid>
      {/* Welcome Section */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h1 className="h3 mb-1">Willkommen zurück, {user?.name}!</h1>
              <p className="text-muted mb-0">
                Hier siehst du, was heute mit deinen Prompts passiert.
              </p>
            </div>
            <Button as={Link} to="/prompts/create" variant="primary">
              <Plus size={16} className="me-2" />
              Prompt erstellen
            </Button>
          </div>
        </Col>
      </Row>

      {/* Stats Cards */}
      <Row className="mb-4">
        {stats.map((stat, index) => (
          <Col md={6} lg={3} key={index} className="mb-3">
            <Card className="border-0 shadow-sm">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="text-muted mb-1">{stat.title}</h6>
                    <h3 className="mb-0 fw-bold">{stat.value}</h3>
                  </div>
                  <div className={`text-${stat.color}`}>
                    {stat.icon}
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      <Row>
        {/* Quick Actions */}
        <Col lg={4} className="mb-4">
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-transparent border-0">
              <h5 className="mb-0">Schnellaktionen</h5>
            </Card.Header>
            <Card.Body>
              <div className="d-grid gap-3">
                {quickActions.map((action, index) => (
                  <Button
                    key={index}
                    as={Link}
                    to={action.link}
                    variant={action.variant}
                    className="d-flex align-items-center justify-content-between p-3"
                  >
                    <div className="text-start">
                      <div className="fw-bold">{action.title}</div>
                      <small>{action.description}</small>
                    </div>
                    {action.icon}
                  </Button>
                ))}
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Recent Prompts */}
        <Col lg={8} className="mb-4">
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-transparent border-0 d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Aktuelle Prompts</h5>
              <Button as={Link} to="/prompts" variant="link" size="sm">
                Alle anzeigen
              </Button>
            </Card.Header>
            <Card.Body>
              {isLoading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Wird geladen...</span>
                  </div>
                </div>
              ) : recentPrompts.length > 0 ? (
                <div className="list-group list-group-flush">
                  {recentPrompts.map((prompt) => (
                    <div key={prompt.id} className="list-group-item border-0 px-0">
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="flex-grow-1">
                          <h6 className="mb-1">
                            <Link to={`/prompts/${prompt.id}`} className="text-decoration-none">
                              {prompt.title}
                            </Link>
                          </h6>
                          <p className="text-muted small mb-2 text-truncate-2">
                            {prompt.description || 'Keine Beschreibung'}
                          </p>
                          <div className="d-flex align-items-center gap-3">
                            <Badge bg={prompt.visibility === 'public' ? 'success' : 'secondary'}>
                              {prompt.visibility === 'public' ? 'Öffentlich' : 'Privat'}
                            </Badge>
                            <small className="text-muted">
                              <Eye size={12} className="me-1" />
                              {prompt.usage?.views || 0}
                            </small>
                            <small className="text-muted">
                              <Copy size={12} className="me-1" />
                              {prompt.usage?.copies || 0}
                            </small>
                            <small className="text-muted">
                              <Heart size={12} className="me-1" />
                              {prompt.usage?.likes || 0}
                            </small>
                          </div>
                        </div>
                        <div className="text-end">
                          <small className="text-muted">
                            {new Date(prompt.createdAt).toLocaleDateString('de-DE')}
                          </small>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4">
                  <i className="fas fa-brain text-muted mb-3" style={{ fontSize: '3rem' }}></i>
                  <h6 className="text-muted">Noch keine Prompts vorhanden</h6>
                  <p className="text-muted small">Erstelle deinen ersten Prompt, um loszulegen</p>
                  <Button as={Link} to="/prompts/create" variant="primary" size="sm">
                    Prompt erstellen
                  </Button>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default DashboardPage;